/**
 * Turnstile Widget-Only Page
 *
 * A minimal page that ONLY renders the Cloudflare Turnstile widget.
 * This is loaded in a small WebView embedded in the native bottom sheet.
 *
 * Communication:
 * - On ready: postMessage({ type: 'ready' })
 * - On success: postMessage({ type: 'success', token: '...' })
 * - On error: postMessage({ type: 'error', error: '...' })
 * - On expired: postMessage({ type: 'expired' })
 */

import React, { useEffect, useState } from 'react'

import { TURNSTILE_SITE_KEY } from '../utils/env'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string
          theme?: 'light' | 'dark' | 'auto'
          callback?: (token: string) => void
          'error-callback'?: (error: string) => void
          'expired-callback'?: () => void
        },
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }
}

function VerifyWidgetPage() {
  const [widgetId, setWidgetId] = useState<string | null>(null)

  // Send message to React Native WebView
  const postToApp = (message: { type: string; token?: string; error?: string }) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message))
    }
  }

  // Initialize Turnstile widget
  useEffect(() => {
    const initTurnstile = () => {
      if (!window.turnstile) {
        // Script not loaded yet, retry
        setTimeout(initTurnstile, 100)
        return
      }

      if (!TURNSTILE_SITE_KEY) {
        postToApp({ type: 'error', error: 'Turnstile not configured' })
        return
      }

      const id = window.turnstile.render('#turnstile-container', {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        callback: (token: string) => {
          postToApp({ type: 'success', token })
        },
        'error-callback': (error: string) => {
          postToApp({ type: 'error', error: error || 'Verification failed' })
        },
        'expired-callback': () => {
          postToApp({ type: 'expired' })
        },
      })

      setWidgetId(id)

      // Tell the app we're ready AFTER widget is rendered
      // Small delay to ensure widget is visible
      setTimeout(() => {
        postToApp({ type: 'ready' })
      }, 100)
    }

    // Load Turnstile script if not already loaded
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = initTurnstile
      document.head.appendChild(script)
    } else {
      initTurnstile()
    }

    return () => {
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div style={styles.container}>
      <div id="turnstile-container" style={styles.widget} />
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    backgroundColor: '#ffffff',
    padding: 0,
    margin: 0,
  },
  widget: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

export default VerifyWidgetPage
